/**
 * Employer-outreach + eligibility hooks (ENR-025/026/027, INT-005).
 *
 * Wraps the Cases service Employer-stage outreach surface:
 *   - GET  /api/v1/cases/{id}/employer-outreach          → EmployerOutreach
 *   - POST /api/v1/cases/{id}/employer-outreach/phone     → log a phone attempt
 *   - POST /api/v1/cases/{id}/employer-outreach/email     → advance the email cadence
 *   - POST /api/v1/cases/{id}/employer-outreach/response  → intake a response + attach
 * plus the integration-engine eligibility check (INT-005):
 *   - POST /api/v1/eligibility/check  { member_id, effective_date? } → IntegrationJob
 *
 * Outreach degrades gracefully: an unimplemented endpoint (404) flips
 * `available = false` so the case page renders a friendly disabled state rather
 * than crashing (mirrors useScreening / useEffectiveDate). The eligibility
 * action surfaces its own `eligibilityError` so a missing connector (404
 * CONNECTOR_NOT_REGISTERED) is shown inline without breaking outreach.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { IntegrationJob } from '../api/types';

/** One logged employer phone attempt. */
export interface EmployerPhoneAttempt {
  id: string;
  outcome: string;
  notes?: string | null;
  created_at: string;
  actor_id?: string | null;
}

/** A status step in the email outreach cadence. */
export interface EmailCadenceStep {
  step: number;
  label: string;
  /** `pending` | `sent` | `responded` | `skipped`. */
  status: string;
  sent_at?: string | null;
}

/** A recorded employer response (with optional attachment reference). */
export interface EmployerResponse {
  id: string;
  channel: string;
  summary?: string | null;
  attachment_ref?: string | null;
  created_at: string;
}

/** The full employer-outreach surface for a case. */
export interface EmployerOutreach {
  phone_attempts: EmployerPhoneAttempt[];
  email_cadence: EmailCadenceStep[];
  /** Index/step of the next email to send, if any. */
  next_email_step?: number | null;
  responses: EmployerResponse[];
}

export interface LogPhoneAttemptBody {
  outcome: string;
  notes?: string;
}

export interface AdvanceEmailBody {
  /** Optional template / step override; the engine advances the cadence. */
  note?: string;
}

export interface IntakeResponseBody {
  channel: string;
  summary?: string;
  attachment_ref?: string;
}

interface UseEmployerOutreachResult {
  outreach: EmployerOutreach | null;
  available: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  logPhoneAttempt: (body: LogPhoneAttemptBody) => Promise<boolean>;
  advanceEmail: (body: AdvanceEmailBody) => Promise<boolean>;
  intakeResponse: (body: IntakeResponseBody) => Promise<boolean>;
  // Eligibility (INT-005) — separate action surface.
  eligibilityJob: IntegrationJob | null;
  eligibilityWorking: boolean;
  eligibilityError: ApiError | null;
  runEligibility: (memberId: string, effectiveDate?: string) => Promise<IntegrationJob | null>;
}

const EMPTY: EmployerOutreach = {
  phone_attempts: [],
  email_cadence: [],
  next_email_step: null,
  responses: [],
};

function normalize(data: Partial<EmployerOutreach> | null): EmployerOutreach {
  return {
    phone_attempts: data?.phone_attempts ?? [],
    email_cadence: data?.email_cadence ?? [],
    next_email_step: data?.next_email_step ?? null,
    responses: data?.responses ?? [],
  };
}

export function useEmployerOutreach(caseId: string | undefined): UseEmployerOutreachResult {
  const [outreach, setOutreach] = useState<EmployerOutreach | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const [eligibilityJob, setEligibilityJob] = useState<IntegrationJob | null>(null);
  const [eligibilityWorking, setEligibilityWorking] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<ApiError | null>(null);

  const base = caseId ? `/cases/${encodeURIComponent(caseId)}/employer-outreach` : null;

  const refresh = useCallback(() => {
    if (!base) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<EmployerOutreach>(base)
      .then((data) => {
        setOutreach(normalize(data));
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setAvailable(false);
          setOutreach(null);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setOutreach(null);
      })
      .finally(() => setIsLoading(false));
  }, [base]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const post = useCallback(
    async (path: string, body: unknown): Promise<boolean> => {
      if (!base) return false;
      setIsWorking(true);
      setActionError(null);
      try {
        const data = await apiClient.post<EmployerOutreach>(`${base}${path}`, body);
        // Engine returns the refreshed outreach surface; fall back to a refetch.
        if (data && (data.phone_attempts || data.email_cadence || data.responses)) {
          setOutreach(normalize(data));
        } else {
          refresh();
        }
        return true;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return false;
      } finally {
        setIsWorking(false);
      }
    },
    [base, refresh]
  );

  const logPhoneAttempt = useCallback((body: LogPhoneAttemptBody) => post('/phone', body), [post]);
  const advanceEmail = useCallback((body: AdvanceEmailBody) => post('/email', body), [post]);
  const intakeResponse = useCallback((body: IntakeResponseBody) => post('/response', body), [post]);

  const runEligibility = useCallback(
    async (memberId: string, effectiveDate?: string): Promise<IntegrationJob | null> => {
      setEligibilityWorking(true);
      setEligibilityError(null);
      try {
        const job = await apiClient.post<IntegrationJob>('/eligibility/check', {
          member_id: memberId,
          ...(effectiveDate ? { effective_date: effectiveDate } : {}),
        });
        setEligibilityJob(job);
        return job;
      } catch (err: unknown) {
        setEligibilityError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return null;
      } finally {
        setEligibilityWorking(false);
      }
    },
    []
  );

  return {
    outreach: outreach ?? (available ? EMPTY : null),
    available,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    logPhoneAttempt,
    advanceEmail,
    intakeResponse,
    eligibilityJob,
    eligibilityWorking,
    eligibilityError,
    runEligibility,
  };
}
